import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Row, Col, Card, Button, Alert } from "react-bootstrap";
import { IActionHandler, DeviceBase, WebAuthnKeyAuthenticate, CredentialInformation, SessionExpiration } from "@preveil-api";
import {
  GlobalErrorMessages, MessageAnchors, useAppDispatch, Message, MessageHandlerDisplayType, useAppSelector, AccountIdentifiers, ForgotPasswordSteps, AccountErrorMessages, KeyStorageAPI,
  KeystorageResponseErrorCodes, dayjs, Helpers, KeyStorageData, AppUserKey, KeyFactory, Device, DevicePlatform, usePostUsersDevicesKeysMutation, useDeleteUsersDevicesKeysByDeviceIdMutation,
  useGetUsersDevicesKeysMutation, QueryParamKeys, normalizeQueryUserId, PublicRoutes, account_types, PlatformTypes, AuthenticationTypes, useAuthContext, collectionApi, RecoveryMethod
} from "src/common";
import { Loading, ErrorMessage, Icon } from "src/components";
import { LoginEmailForm, LoginSMSCodeVerificationForm, LoginTOTPCodeVerificationForm, LoginWebAuthKey } from "../login";
import { ChoosePanel, CreateNewPasswordForm, EmailCodeVerificationForm, PasteRecoveryCodeForm, RecoveryNotSetup } from ".";
import { uiActions, accountActions } from "src/store";
import { getLoginWebStepOnServerError } from "src/store/account/saga";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

/* Description:  Component that handles the flow when the user forgets their password and wants to reset it. */
function ForgotPasswordComponent() {
  const [search_params, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [getUsersApproversSecret] = collectionApi.endpoints.getUsersApproversSecret.useLazyQuery();
  const account_ids = useAppSelector((state: RootState) => state.account.account_ids);
  const redirect_url = useAppSelector((state: RootState) => state.ui.redirect_url);
  const current_account = useAppSelector((state: RootState) => state.account.current_account);
  const step = useAppSelector((state: RootState) => state.account.status);
  const [verification_error_count, setVerificationErrorCount] = useState<number>(0);
  const [enter_email, setEnterEmail] = useState<boolean>(true);
  const [email_code, setEmailCode] = useState<string>();
  const [server_shard, setServerShard] = useState<string>();
  const [session_expiration, setSessionExpiration] = useState<SessionExpiration>();
  const [user_key, setUserKey] = useState<AppUserKey>();
  const [user_id, setUserId] = useState<string>();
  const credential_info_ref = useRef<CredentialInformation>();
  const phone_number_ref = useRef<string>();
  const two_factor_type = useRef<AuthenticationTypes>();
  const [getDevices] = useGetUsersDevicesKeysMutation();
  const [deleteDevice] = useDeleteUsersDevicesKeysByDeviceIdMutation();
  const [addDevice] = usePostUsersDevicesKeysMutation();
  const { loginComplete } = useAuthContext();

  useEffect(() => {
    dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.LOADING));
    const query_user_id = normalizeQueryUserId(search_params.get(QueryParamKeys.USER_ID_QUERY_KEY));
    if (!!query_user_id) {
      setEnterEmail(false);
      setUserId(query_user_id);
      search_params.delete(QueryParamKeys.USER_ID_QUERY_KEY);
      setSearchParams(search_params);
    } else {
      setEnterEmail(true);
      dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.SUBMIT_EMAIL));
    }
  }, []);

  useEffect(() => {
    if (!!user_id) {
      dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.CHOOSE_RECOVERY_METHOD));
    }
  }, [user_id]);

  // Description: Complete step after KSS User creds
  useEffect(() => {
    !!account_ids && dispatch(accountActions.findCollectionServerCurrentUser(account_ids));
  }, [account_ids]);

  // Description: Update the login status when current account is set in state
  useEffect(() => {
    (!!current_account || !!redirect_url) &&
      loginComplete(current_account, account_types.express, redirect_url);
  }, [current_account, redirect_url]);

  /* Description:  Get Server Shard for user recovery */
  function getServerShard(email_code?: string, sms_code?: string, totp_code?: string, web_auth_key?: WebAuthnKeyAuthenticate) {
    if (!!user_id) {
      KeyStorageAPI.getServerShard(user_id, { email_code, sms_code, totp_code, web_auth_key })
        .then((response) => {
          if (!response.isError) {
            setServerShard(response.data.server_shard);
            setSessionExpiration({ valid_until: response.data.session_expiration.timestamp, signature: response.data.session_expiration.signature });
            if (!!response.data.server_shard) {
              dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.PASTE_RECOVERY_CODE));
            } else {
              dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.NO_RECOVERY_SETUP));
            }
          } else {
            const message_code = response.data.error || response.data.bad_parameter || "";
            const two_factor_codes = [KeystorageResponseErrorCodes.no_sms_auth_header, KeystorageResponseErrorCodes.no_totp_auth_header, KeystorageResponseErrorCodes.no_webauthn_header];
            if (message_code === KeystorageResponseErrorCodes.no_email_auth_header) {
              dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.SUBMIT_EMAIL_CODE));
            } else if (two_factor_codes.includes(message_code)) {
              if (message_code === KeystorageResponseErrorCodes.no_sms_auth_header) {
                const sms_header = response.headers.get("Www-Authenticate") as string;
                const _phone_number: string | undefined = sms_header.match(/phone_number=([+\d]+)/)?.[1] || undefined;
                phone_number_ref.current = _phone_number;
                two_factor_type.current = AuthenticationTypes.SMS;
                dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.SUBMIT_SMS_CODE));
              } else if (message_code === KeystorageResponseErrorCodes.no_totp_auth_header) {
                two_factor_type.current = AuthenticationTypes.TOTP;
                dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.SUBMIT_TOTP_CODE));
              } else if (message_code === KeystorageResponseErrorCodes.no_webauthn_header) {
                const authenticate_headers = response.headers.get("Www-Authenticate") as string; // get challenge and credential ID from the backend
                const parsed_data = Object.fromEntries(
                  authenticate_headers.replace(/^webauthn\s*/, "") // Remove "webauthn" prefix
                    .split(";")
                    .map(part => part.trim().split(/=(.+)/)) // Split at the first '=' only
                );
                credential_info_ref.current = parsed_data as CredentialInformation;
                dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.WEB_AUTH_LOGIN));
              }
            } else if (message_code === KeystorageResponseErrorCodes.bad_email_code) {
              if (verification_error_count === 4) {
                dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.bad_code_reset)));
                setVerificationErrorCount(0);
                dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.SUBMIT_EMAIL));
              } else {
                setVerificationErrorCount(verification_error_count + 1);
                dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.bad_verification_code)));
              }
            } else {
              const step_error = getLoginWebStepOnServerError(message_code);
              const message = _.has(AccountErrorMessages, message_code) ? (AccountErrorMessages as any)[message_code] : AccountErrorMessages.default;
              dispatch(accountActions.setComponentStatus(step_error));
              dispatch(uiActions.handleRequestErrors(new Message(message)));
              step_error === ForgotPasswordSteps.SUBMIT_EMAIL && ForgotPasswordRequests.handleResetLogin();
            }
          }
        }).catch((error) => {
          dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.error_resetting_password), error));
          ForgotPasswordRequests.handleResetLogin();
        });
    }
  }

  /* Description: Creates a new device key and recovers the users account with the new password and auth token. */
  async function recoverExpressUser(auth_token: string, pass: string) {
    const device_key = await KeyFactory.newUserKey();
    const kss_device: DeviceBase = await Device.createNewJSONLocalDevice(DevicePlatform.express.text, PlatformTypes.express, device_key, {}, session_expiration);
    if (!!user_id && !!user_key) {
      const account_ids = {
        user_id,
        user_key,
        device_id: kss_device.device_id,
        device_key,
        user_key_serialized: Helpers.b64Encode(user_key.serialize()),
        device_key_serialized: Helpers.b64Encode(device_key.serialize()),
        phone_number: phone_number_ref.current,
        two_factor_type: two_factor_type.current
      };
      const wrapped_data = await KeyStorageData.wrapKSSData(pass, user_key, device_key, 1, account_ids.device_id);
      KeyStorageAPI.rekeyUser(account_ids, undefined, wrapped_data, undefined, Helpers.b64Encode(user_key.public_user_key.verify_key.public_key), auth_token).then((response) => {
        if (!response.isError) {
          addNewDevice(account_ids, kss_device, auth_token);
        } else {
          const message = response.data.bad_parameter === AccountErrorMessages.previous_password_existed ? AccountErrorMessages.cannot_user_previous_password : AccountErrorMessages.error_changing_password;
          dispatch(uiActions.handleRequestErrors(new Message(message), response.data));
          response.data.bad_parameter === AccountErrorMessages.previous_password_existed ? dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.CREATE_NEW_PASSWORD)) : ForgotPasswordRequests.handleResetLogin();
        }
      }).catch((error) => {
        dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.default), error));
        ForgotPasswordRequests.handleResetLogin();
      });
    }
  }

  /* Description: Adds a new device since we needed to create a new one in order to recover the account */
  function addNewDevice(_account_ids: AccountIdentifiers, device: DeviceBase, auth_token: string) {
    if (!!user_id) {
      const request = {
        account_ids: _account_ids,
        body: {
          device,
          user_id
        },
      };
      addDevice(request).then(() => {
        deviceCleanUp(_account_ids, device, auth_token);
      }).catch((error: any) => {
        dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.restore_account_error), error));
        ForgotPasswordRequests.handleResetLogin();
      });
    }
  }

  /* Description: After creating a new device key, we must delete the other devices 
  we first get all the devices and delete the ones that are not the current one. */
  function deviceCleanUp(_account_ids: AccountIdentifiers, device: DeviceBase, auth_token: string) {
    const request = {
      account_ids: _account_ids,
      body: {
        userId: _account_ids.user_id,
      },
    };
    getDevices(request).then((response: any) => {
      const non_current_devices = response.data.devices.filter((_device: any) => _device.device_id !== device.device_id);
      if (non_current_devices.length === 0) {
        login(_account_ids, auth_token);
        return;
      }
      const delete_requests = non_current_devices.map((_device: any) => {
        const delete_request = {
          account_ids: _account_ids,
          body: {
            user_id: _account_ids.user_id,
            device_id: _device.device_id,
          },
        };
        // NOTE: Swallow individual failures so one bad delete doesn't reject the whole batch
        return deleteDevice(delete_request).catch((error: any) => {
          dispatch(uiActions.handleRequestErrors(new Message("Error Deleting Device", MessageHandlerDisplayType.logger), error));
        });
      });
      // NOTE: Only log in once every delete has settled, regardless of resolution order
      Promise.all(delete_requests).then(() => {
        login(_account_ids, auth_token);
      });
    }).catch((error: any) => {
      dispatch(uiActions.handleRequestErrors(new Message("Error Deleting Devices", MessageHandlerDisplayType.logger), error));
      login(_account_ids, auth_token);
    });
  }

  function login(_account_ids: AccountIdentifiers, auth_token: string) {
    dispatch((accountActions.handleSetAuthToken(auth_token)));
    dispatch(accountActions.getKssUserSuccess(_account_ids));
  }

  // Description: Handle all children component actions and store it
  const ForgotPasswordRequests = {
    handleSubmitEmail: (user_email: string) => {
      dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.LOADING));
      setUserId(user_email);
    },
    handleChooseRecoveryMethod: (method: RecoveryMethod) => {
      switch (method) {
        case RecoveryMethod.File:
          getServerShard();
          break;
        case RecoveryMethod.Group:
          if (!!user_id) {
            getUsersApproversSecret({ userId: user_id });
            dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.RECOVERY_EMAIL_SENT));
          }
      }
    },
    // Description: (** FOR PASSWORD RECOVERY) Combine the server shard and the user shard to get the user key and set it. 
    // Continue on to next step - create a new password
    handleSubmitRecoverCode: async (user_key?: AppUserKey) => {
      if (!!server_shard) {
        dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.LOADING));
        if (!!user_key) {
          setUserKey(user_key);
          dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.CREATE_NEW_PASSWORD));
        } else {
          dispatch(uiActions.handleRequestErrors(new Message("Wrong Recovery Code. Please try again.")));
          dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.PASTE_RECOVERY_CODE));
        }
      }
    },
    handleSubmitSMSVerificationCode: (sms_code: string) => {
      dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.LOADING));
      getServerShard(email_code, sms_code);
    },
    handleSubmitTOTPVerificationCode: (totp_code: string) => {
      dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.LOADING));
      getServerShard(email_code, undefined, totp_code); // NOTE: if the user is in the forgot password flow then we need to get the server shard
    },
    handleSubmitWebAuthnKey: (params: { web_auth_key: WebAuthnKeyAuthenticate, type: AuthenticationTypes }) => {
      const { web_auth_key, type } = params;
      two_factor_type.current = type;
      dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.LOADING));
      getServerShard(email_code, undefined, undefined, web_auth_key);
    },
    // Description: creates a new auth token with the new password and 
    // rekeys the user with the new password and the new auth token.
    handleSubmitNewPassword: async (pass: string) => {
      if (!!user_key && !!user_id) {
        dispatch(accountActions.setComponentStatus(ForgotPasswordSteps.LOADING));
        const token = await KeyStorageData.authenticationToken(user_id, pass);
        const auth_token = Helpers.b64Encode(token);
        recoverExpressUser(auth_token, pass);
      }
    },
    handleSubmitVerificationCode: (params: { secret: string, user_id: string }) => {
      setEmailCode(params.secret);
      getServerShard(params.secret, undefined, undefined);
    },
    // Description: Reset all form states and go back to Login page
    handleResetLogin: () => {
      setEmailCode(undefined);
      setServerShard(undefined);
      setSessionExpiration(undefined);
      setUserKey(undefined);
      setUserId(undefined);
      setEnterEmail(true);
      two_factor_type.current = undefined;
      credential_info_ref.current = undefined;
      navigate(`/${PublicRoutes.login_route}`); // Redirect to login page
    },
    handlePageError: (message: string) => {
      ForgotPasswordRequests.handleResetLogin();
      dispatch(uiActions.handleRequestErrors(new Message(message)));
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
    }
  };

  //  Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    dispatch(uiActions.handleMessageDismiss());
    const callback = `handle${action_obj.actionType}`;
    // Handle local calls:
    if ((ForgotPasswordRequests as any)[callback] instanceof Function) {
      (ForgotPasswordRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      ForgotPasswordRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  const step_display_numbers: { [key: number]: number } = {
    [ForgotPasswordSteps.SUBMIT_EMAIL]: 1,
    [ForgotPasswordSteps.CHOOSE_RECOVERY_METHOD]: 1,
    [ForgotPasswordSteps.SUBMIT_EMAIL_CODE]: enter_email ? 2 : 1,
    [ForgotPasswordSteps.SUBMIT_SMS_CODE]: enter_email ? 3 : 2,
    [ForgotPasswordSteps.SUBMIT_TOTP_CODE]: enter_email ? 3 : 2,
    [ForgotPasswordSteps.PASTE_RECOVERY_CODE]: enter_email ? 4 : 3,
    [ForgotPasswordSteps.CREATE_NEW_PASSWORD]: enter_email ? 5 : 4,
    [ForgotPasswordSteps.WEB_AUTH_LOGIN]: enter_email ? 3 : 2,
    [ForgotPasswordSteps.RECOVERY_EMAIL_SENT]: 5,
    [ForgotPasswordSteps.NO_RECOVERY_SETUP]: 1,
  };

  // Description: Render the correct form
  function ForgotPasswordForms() {
    switch (step) {
      case ForgotPasswordSteps.SUBMIT_EMAIL:
        return <Card.Body className="p-3 m-1">
          <LoginEmailForm handleAction={handlePageActions} forgot_password={true}>
            <p>Please enter your <b>PreVeil Express user ID</b> to get back into your account.</p>
          </LoginEmailForm>
        </Card.Body>;
      case ForgotPasswordSteps.CHOOSE_RECOVERY_METHOD:
        return <ChoosePanel handleAction={handlePageActions} />;
      case ForgotPasswordSteps.SUBMIT_EMAIL_CODE:
        return !!user_id ?
          <EmailCodeVerificationForm user_id={user_id} expires={dayjs().add(15, "minutes").format("YYYY-MM-DD HH:mm:ss")}
            handleAction={handlePageActions} /> : <FPErrorMessage />;
      case ForgotPasswordSteps.SUBMIT_SMS_CODE:
        return <Card.Body className="p-3 m-1">
          <LoginSMSCodeVerificationForm handleAction={handlePageActions} forgot_password={true} />
        </Card.Body>;
      case ForgotPasswordSteps.SUBMIT_TOTP_CODE:
        return <Card.Body className="p-3 m-1">
          <LoginTOTPCodeVerificationForm handleAction={handlePageActions} forgot_password={true} />
        </Card.Body>;
      case ForgotPasswordSteps.WEB_AUTH_LOGIN:
        return !!credential_info_ref.current ? <Card.Body className="p-3 m-1">
          <LoginWebAuthKey handleAction={handlePageActions} webauth_info={credential_info_ref.current} forgot_password={true} />
        </Card.Body> : <FPErrorMessage />;
      case ForgotPasswordSteps.PASTE_RECOVERY_CODE:
        return (!!server_shard && !!session_expiration) ?
          <PasteRecoveryCodeForm server_shard={server_shard} handleAction={handlePageActions} /> :
          <FPErrorMessage />;
      case ForgotPasswordSteps.CREATE_NEW_PASSWORD:
        return <CreateNewPasswordForm user_identifier={!!user_id ? { user_id } : undefined} handleAction={handlePageActions} />;
      case ForgotPasswordSteps.RECOVERY_EMAIL_SENT:
        return <Card.Body className="p-3 m-1">
          <p className="fs-3">
            <b>On this computer</b> check <b>{user_id}</b> for an email from PreVeil and follow its instructions.
          </p>
          <Alert variant="warning">
            <p className="d-flex"><Icon className="pv-icon-question" />
              We sent you an email to verify that you are the owner of this email account.
              You must use this computer to follow the instructions in the email.
            </p>
          </Alert>
          <Button className="mt-3" href={`/${PublicRoutes.get_started_route}`}>Go Back</Button>
        </Card.Body>;
      case ForgotPasswordSteps.NO_RECOVERY_SETUP:
        return <RecoveryNotSetup handleAction={handlePageActions} />;
      default:
        return <Loading className="in-place" />;
    }
  }

  // Description: Alternate card for errors
  function FPErrorMessage() {
    return <Card.Body className="p-3 m-1">
      <ErrorMessage message={GlobalErrorMessages.default} className="fs-4" />
    </Card.Body>;
  }

  return step > ForgotPasswordSteps.LOADING ?
    <Row className="justify-content-center">
      <Col md={7} className={step === 1 ? "max-400 mt-5" : ""}>
        <h1 className="main-header">Forgot Your Password?{step !== ForgotPasswordSteps.NO_RECOVERY_SETUP && <small>{step_display_numbers[step]} of {enter_email ? "6" : "5"}</small>}</h1>
        <Card className="pv-card">
          <ForgotPasswordForms />
        </Card>
      </Col>
    </Row> : <Loading />;
};

export default React.memo(ForgotPasswordComponent);
